// AGC: GeometryMethodSelection - Step 2: Choose how to define field boundaries
// AGC: Two options: GPS path walking or manual polygon drawing on satellite map

import React from 'react'
import { Navigation, Pencil, MapPin, Map as MapIcon } from 'lucide-react'
import { TopBar, GlassCard } from '../shared'
import type { Screen, PlotFormData } from '../../types'

interface GeometryMethodSelectionProps {
  plotData: PlotFormData
  onNavigate: (screen: Screen, data?: { plotData?: PlotFormData }) => void
  onBack: () => void
}

/**
 * AGC: GeometryMethodSelection - Choose geometry definition method
 * AGC: Two options:
 * AGC: 1. GPS Path - Walk the boundary using simulated GPS
 * AGC: 2. Manual Drawing - Tap to add vertices on satellite map
 */
export const GeometryMethodSelection: React.FC<GeometryMethodSelectionProps> = ({ 
  plotData, 
  onNavigate, 
  onBack 
}) => {

  // AGC: Navigate to GPS path geometry screen
  const handleGPSOption = () => {
    onNavigate('geometry-gps', { plotData })
  }

  // AGC: Navigate to manual polygon drawing screen
  const handleMapOption = () => {
    onNavigate('geometry-manual', { plotData })
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* AGC: Top Bar */}
      <TopBar 
        title="Definir Limites"
        subtitle="Passo 2 de 4"
        onBack={onBack}
      />

      {/* AGC: Progress indicator */}
      <div className="px-4 py-3">
        <div className="flex gap-2">
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-gray-200" />
          <div className="flex-1 h-1 rounded-full bg-gray-200" />
        </div>
      </div>

      {/* AGC: Field name display */}
      <div className="px-4 py-2">
        <GlassCard className="bg-emerald-50 border-emerald-200" padding="sm">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-800">
              Talhão: {plotData.name}
            </span>
          </div>
        </GlassCard>
      </div>

      {/* AGC: Instructions */}
      <div className="px-4 py-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Como deseja definir os limites?
        </h3>
        <p className="text-gray-600 text-sm">
          Escolha o método mais adequado para delimitar a área do seu talhão.
        </p>
      </div>

      {/* AGC: Method Options */}
      <div className="px-4 space-y-4">
        {/* AGC: Option 1 - GPS Path */}
        <button
          onClick={handleGPSOption}
          className="w-full text-left"
          data-testid="gps-option-button"
        >
          <GlassCard 
            className="border-2 border-transparent hover:border-[#0B5F4A] transition-all duration-200"
            hover
          >
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                <Navigation className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-900">
                  Definir com GPS
                </h4>
                <p className="text-gray-600 text-sm mt-1">
                  Caminhe o limite do lote usando o GPS (simulado).
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    📍 Automático
                  </span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    ~5 min
                  </span>
                </div>
              </div>
            </div>
          </GlassCard>
        </button>

        {/* AGC: Option 2 - Manual Drawing */}
        <button
          onClick={handleMapOption}
          className="w-full text-left"
          data-testid="map-option-button"
        >
          <GlassCard 
            className="border-2 border-transparent hover:border-[#0B5F4A] transition-all duration-200"
            hover
          >
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                <Pencil className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-900">
                  Desenhar no mapa
                </h4>
                <p className="text-gray-600 text-sm mt-1">
                  Marque os vértices do lote na imagem de satélite.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                    ✏️ Manual
                  </span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    ~2 min
                  </span>
                </div>
              </div>
            </div>
          </GlassCard>
        </button>
      </div>

      {/* AGC: Help card */}
      <div className="px-4 mt-6">
        <GlassCard className="bg-amber-50 border-amber-200">
          <div className="flex gap-3">
            <span className="text-xl">💡</span>
            <div>
              <p className="font-medium text-amber-800">Dica</p>
              <p className="text-sm text-amber-700 mt-1">
                Para áreas grandes e irregulares, o <strong>GPS</strong> é mais prático.
                Para áreas pequenas ou quando você já conhece os vértices, 
                <strong> desenhar no mapa</strong> é mais rápido.
              </p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* AGC: Simulation mode notice */}
      <div className="px-4 mt-4 pb-8">
        <GlassCard className="bg-gray-100 border-gray-300">
          <div className="flex gap-3">
            <MapIcon className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-600">
                <strong>Modo de demonstração:</strong> O GPS será simulado com 
                coordenadas fictícias para fins de teste.
              </p>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  )
}

export default GeometryMethodSelection






